import {DICTIONARY} from '../../../../dictionary';

const dic = DICTIONARY;

export interface FlowNode {
	id: string;
	x: number;
	y: number;
	w?: number;
	h?: number;
	icon: string;
	title: string;
	sub?: string;
	sub2?: string;
	tone?: string;
	badge?: string;
	step?: string | number; // for steps that happen inside a node (e.g. scanning) rather than on an edge
	tags?: string[];
}

export interface FlowEdge {
	from: string;
	to: string;
	fromSide?: string;
	toSide?: string;
	tone?: string;
	dashed?: boolean;
	step?: string | number;
	label?: string;
	dots?: number;
	dur?: number;
	delay?: number;
	labelAt?: number;
	elbow?: boolean;
}

export interface FlowSpec {
	aria: string;
	height: number;
	archLabel: string;
	nodes: FlowNode[];
	edges: FlowEdge[];
	steps: {n: string | number, t: string, threat?: boolean}[];
	facts: {k: string, v: string, mono?: boolean}[];
	note: string;
}

export interface EmailFlowResult {
	state: 'connected' | 'partial' | 'none';
	platform: 'm365' | 'google' | 'other';
	platformLabel: string;
	spec?: FlowSpec;
}

const PLAT_LABEL = {m365: 'Microsoft 365', google: 'Google Workspace', other: 'On-prem / other SMTP'};

const truncate = (str: string, max: number = 30): string =>
	str && str.length > max ? str.slice(0, max - 1) + '…' : (str || '');

function detectPlatform(planInfo: any): 'm365' | 'google' | 'other' {
	if (planInfo.exchange_server?.enabled) {
		return 'm365';
	}
	if (planInfo.gsuite_server?.enabled) {
		return 'google';
	}
	return 'other';
}

function platNode(plat: string, x: number, y: number, opts: any = {}): FlowNode {
	if (plat === 'm365') {
		return {id: opts.id || 'srv', x, y, icon: 'ms', title: opts.title || 'Microsoft 365',
			sub: opts.sub !== undefined ? opts.sub : (opts.onpremises ? 'Exchange on-premises' : 'Exchange Online'), tone: 'brand', badge: opts.badge, w: opts.w};
	}
	if (plat === 'google') {
		return {id: opts.id || 'srv', x, y, icon: 'google', title: opts.title || 'Google Workspace',
			sub: opts.sub !== undefined ? opts.sub : 'Gmail', tone: 'brand', badge: opts.badge, w: opts.w};
	}
	return {id: opts.id || 'srv', x, y, icon: 'server', title: opts.title || 'Your mail server',
		sub: opts.sub !== undefined ? opts.sub : 'on-prem / hosted MTA', tone: 'brand', badge: opts.badge, w: opts.w};
}

/* ============================ INBOUND ============================ */

export function buildInboundFlow(planInfo: any, brand: string): EmailFlowResult {
	const platform = detectPlatform(planInfo);
	const platformLabel = PLAT_LABEL[platform];
	const relay = planInfo.inbound_relay || {};
	const arch = relay.sending_architecture;
	const psBadge = planInfo.powershell?.enabled && platform === 'm365' ? 'PowerShell managed' : null;
	const shieldName = brand + ' Inbound Shield';

	const mtaRoutes = (relay.mtas || []).map(m => `${m.host}:${m.port || 25}`);
	const mtaFact = mtaRoutes.length ? mtaRoutes.slice(0, 2).join(', ') + (mtaRoutes.length > 2 ? ` +${mtaRoutes.length - 2}` : '') : null;
	const ipsFact = relay.static_ip_set_ips?.length ?
		relay.static_ip_set_ips.slice(0, 2).join(', ') + (relay.static_ip_set_ips.length > 2 ? ' …' : '') : null;
	const mxHost = planInfo.mx || 'inbound-smtp.us-east-1.amazonaws.com';
	const onpremises = !!planInfo.exchange_server?.onpremises;
	const graphEndpoint = (planInfo.exchange_server?.msgraph_endpoint || 'graph.microsoft.com/v1.0').replace(/^https?:\/\//, '');
	const quarantineNode: FlowNode = {id: 'q', icon: 'quarantine', title: 'Quarantine', tone: 'danger', w: 132, h: 78, x: 0, y: 0};

	const specs: {[arch: string]: () => FlowSpec} = {

		[dic.CONSTANTS.inboundSendingArchitecture.mx]: () => ({
			aria: 'Inbound MX flow', height: 330, archLabel: 'MX records',
			nodes: [
				{id: 'snd', x: 20, y: 50, icon: 'sender', title: 'External sender', sub: 'any origin'},
				{id: 'tru', x: 250, y: 35, icon: 'shield', title: shieldName, sub: truncate(mxHost, 38), tone: 'trustifi', w: 190, step: 2, tags: ['Headers', 'Body', 'Links', 'Files', 'AI']},
				platNode(platform, 530, 50, {badge: psBadge, w: 170, onpremises}),
				{id: 'box', x: 790, y: 50, icon: 'mailbox', title: 'Recipient mailbox', tone: 'success', w: 160},
				{...quarantineNode, x: 280, y: 240}
			],
			edges: [
				{from: 'snd', to: 'tru', step: 1, label: 'via MX', dur: 2.8},
				{from: 'tru', to: 'srv', step: 3, tone: 'success', label: 'clean mail', dur: 2.8, delay: .9},
				{from: 'srv', to: 'box', step: 4, tone: 'success', dur: 2.4, delay: 1.4},
				{from: 'tru', to: 'q', fromSide: 'bottom', toSide: 'top', tone: 'danger', dashed: true, step: '!', label: 'threats held', dots: 1, dur: 3.6},
				{from: 'q', to: 'srv', fromSide: 'right', toSide: 'bottom', tone: 'success', dashed: true, label: 'Release to MTA', elbow: true}
			],
			steps: [
				{n: 1, t: `Your domain's MX records point to ${brand}, so all incoming email arrives at ${brand} first.`},
				{n: 2, t: 'Every message is scanned in real time — anti-virus, link analysis, impersonation and AI content checks.'},
				{n: 3, t: 'Clean mail is delivered to your mail server over the configured route.'},
				{n: 4, t: 'The message lands in the recipient\'s mailbox — with threat banners applied if configured.'},
				{n: '!', t: `Malicious mail never reaches your server — it is held in the ${brand} quarantine for review.`, threat: true}
			],
			facts: [
				{k: 'MX target', v: mxHost, mono: true},
				mtaFact ? {k: 'Delivery route', v: mtaFact, mono: true} : null,
				ipsFact ? {k: 'Scanning IPs', v: ipsFact, mono: true} : null
			].filter(Boolean),
			note: `${brand} sits in front of your mail server — threats are stopped before they ever reach your environment.`
		}),

		[dic.CONSTANTS.inboundSendingArchitecture.connector]: () => ({
			aria: 'Inbound connector flow', height: 330, archLabel: 'Connector (inline)',
			nodes: [
				{id: 'snd', x: 20, y: 50, icon: 'sender', title: 'External/Internal sender', sub: 'any origin', w: 140},
				platNode(platform, 220, 50, {badge: psBadge, w: 168, onpremises}),
				{id: 'tru', x: 470, y: 35, icon: 'shield', title: shieldName, sub: 'inbound-smtp.trustifi.com', sub2: 'auth: relay key + secret', tone: 'trustifi', w: 190, step: 3, tags: ['Headers', 'Body', 'Links', 'Files', 'AI']},
				platNode(platform, 740, 50, {id: 'srv2', title: platform === 'other' ? 'Your mail server' : platformLabel, sub: 'inbound connector', w: 168}),
				{id: 'box', x: 960, y: 54, icon: 'mailbox', title: 'Mailbox', tone: 'success', w: 90, h: 84},
				{...quarantineNode, x: 500, y: 240}
			],
			edges: [
				{from: 'snd', to: 'srv', step: 1, label: 'MX unchanged', dur: 2.6},
				{from: 'srv', to: 'tru', step: 2, label: 'via connector', dur: 2.6, delay: .6},
				{from: 'tru', to: 'srv2', step: 4, tone: 'success', label: 'returned clean', dur: 2.6, delay: 1.2},
				{from: 'srv2', to: 'box', tone: 'success', dots: 1, dur: 2.2, delay: 1.8},
				{from: 'tru', to: 'q', fromSide: 'bottom', toSide: 'top', tone: 'danger', dashed: true, step: '!', label: 'threats held', dots: 1, dur: 3.6},
				{from: 'q', to: 'srv2', fromSide: 'right', toSide: 'bottom', tone: 'success', dashed: true, label: 'Release to MTA', elbow: true}
			],
			steps: [
				{n: 1, t: 'Mail arrives at your platform as usual — MX records stay unchanged.'},
				{n: 2, t: `A transport rule routes each message out through a connector to ${brand}'s smart host, authenticated with your relay credentials.`},
				{n: 3, t: `${brand} scans the message inline — links, attachments, impersonation, AI analysis.`},
				{n: 4, t: 'Clean mail returns through your inbound connector and is delivered to the mailbox.'},
				{n: '!', t: 'Threats are held in quarantine instead of being returned.', threat: true}
			],
			facts: [
				{k: 'Smart host', v: 'inbound-smtp.trustifi.com', mono: true},
				mtaFact ? {k: 'Return route', v: mtaFact, mono: true} : null,
				ipsFact ? {k: brand + ' IPs', v: ipsFact, mono: true} : null
			].filter(Boolean),
			note: 'Inline protection without touching MX records — the scanning loop adds seconds, not minutes.'
		}),

		[dic.CONSTANTS.inboundSendingArchitecture.graph]: () => ({
			aria: 'Inbound API flow', height: 352, archLabel: 'Microsoft Graph API',
			nodes: [
				{id: 'snd', x: 20, y: 42, icon: 'sender', title: 'External/Internal sender', sub: 'any origin', w: 150},
				platNode(platform, 300, 42, {badge: psBadge, w: 180, onpremises}),
				{id: 'box', x: 830, y: 42, icon: 'mailbox', title: 'Recipient mailbox', tone: 'success', w: 160},
				{id: 'tru', x: 310, y: 222, icon: 'shield', title: shieldName, sub: 'Graph subscription (webhook)', tone: 'trustifi', w: 200, h: 92},
				{...quarantineNode, x: 640, y: 230}
			],
			edges: [
				{from: 'snd', to: 'srv', step: 1, dur: 2.6},
				{from: 'srv', to: 'box', step: 2, tone: 'success', label: 'delivered immediately', dur: 2.6, delay: .5},
				{from: 'srv', to: 'tru', fromSide: 'bottom', toSide: 'top', step: 3, label: 'new-mail notification', dur: 2.8, delay: .9, labelAt: .45},
				{from: 'tru', to: 'q', tone: 'danger', dashed: true, step: '!', label: 'moved to quarantine', dots: 1, dur: 3.4}
			],
			steps: [
				{n: 1, t: 'Mail flows to Microsoft 365 exactly as before — nothing is re-routed.'},
				{n: 2, t: 'The message is delivered to the mailbox immediately.'},
				{n: 3, t: `A Microsoft Graph subscription notifies ${brand} the moment a message arrives; the message is fetched and scanned via API.`},
				{n: '!', t: 'If a threat is found, the message is instantly pulled from the inbox and moved to quarantine.', threat: true}
			],
			facts: [
				{k: 'API', v: graphEndpoint, mono: true},
				{k: 'Trigger', v: 'new-mail subscription (webhook)', mono: true},
				{k: 'Mailboxes', v: 'managed in Mailbox Management'}
			],
			note: `Email never leaves your tenant — ${brand} works entirely through the Microsoft Graph API.`
		}),

		googleApi: () => ({
			aria: 'Inbound API flow', height: 352, archLabel: 'Gmail API',
			nodes: [
				{id: 'snd', x: 20, y: 42, icon: 'sender', title: 'External/Internal sender', sub: 'any origin', w: 150},
				platNode(platform, 300, 42, {w: 180}),
				{id: 'box', x: 830, y: 42, icon: 'mailbox', title: 'Recipient mailbox', tone: 'success', w: 160},
				{id: 'tru', x: 310, y: 222, icon: 'shield', title: shieldName, sub: 'Gmail API · gmail.modify', tone: 'trustifi', w: 200, h: 92},
				{...quarantineNode, x: 640, y: 230}
			],
			edges: [
				{from: 'snd', to: 'srv', step: 1, dur: 2.6},
				{from: 'srv', to: 'box', step: 2, tone: 'success', label: 'delivered immediately', dur: 2.6, delay: .5},
				{from: 'srv', to: 'tru', fromSide: 'bottom', toSide: 'top', step: 3, label: 'new-mail check', dur: 2.8, delay: .9, labelAt: .45},
				{from: 'tru', to: 'q', tone: 'danger', dashed: true, step: '!', label: 'moved via API', dots: 1, dur: 3.4}
			],
			steps: [
				{n: 1, t: 'Mail flows to Google Workspace exactly as before — nothing is re-routed.'},
				{n: 2, t: 'The message is delivered to the mailbox immediately.'},
				{n: 3, t: `${brand} is notified through the Gmail API and fetches the message for scanning within seconds.`},
				{n: '!', t: 'If a threat is found, the message is instantly pulled from the inbox via the Gmail API.', threat: true}
			],
			facts: [
				{k: 'API', v: 'gmail.googleapis.com', mono: true},
				{k: 'Connection', v: 'service account'},
				{k: 'Mailboxes', v: 'managed in Mailbox Management'}
			],
			note: `Email never leaves your Google Workspace — ${brand} works entirely through the Gmail API.`
		}),

		[dic.CONSTANTS.inboundSendingArchitecture.journal]: () => ({
			aria: 'Inbound journaling flow', height: 352, archLabel: 'Journaling (monitor)',
			nodes: [
				{id: 'snd', x: 20, y: 42, icon: 'sender', title: 'External/Internal sender', sub: 'any origin', w: 150},
				platNode(platform, 300, 42, {badge: psBadge, w: 180, onpremises}),
				{id: 'box', x: 830, y: 42, icon: 'mailbox', title: 'Recipient mailbox', tone: 'success', w: 160},
				{id: 'tru', x: 310, y: 222, icon: 'shield', title: shieldName, sub: truncate(planInfo.journalEmailAddress, 34), tone: 'trustifi', w: 216, h: 92},
				{id: 'act', x: 680, y: 230, icon: 'api', title: 'Threat response', sub: 'alerts · remediation', tone: 'danger', w: 150, h: 78}
			],
			edges: [
				{from: 'snd', to: 'srv', step: 1, dur: 2.6},
				{from: 'srv', to: 'box', step: 2, tone: 'success', label: 'delivered normally', dur: 2.6, delay: .5},
				{from: 'srv', to: 'tru', fromSide: 'bottom', toSide: 'top', step: 3, dashed: true, label: 'journal copy', dur: 2.8, delay: .9, labelAt: .45},
				{from: 'tru', to: 'act', tone: 'danger', dashed: true, step: 4, dots: 1, dur: 3.4}
			],
			steps: [
				{n: 1, t: `Mail flows to ${platformLabel} and on to mailboxes — delivery is completely untouched.`},
				{n: 2, t: 'The message is delivered to the recipient as usual.'},
				{n: 3, t: `A journaling rule sends a copy of every message to your dedicated ${brand} journal address.`},
				{n: 4, t: `${brand} scans each copy; detections raise alerts and threat-response actions.`, threat: true}
			],
			facts: [
				{k: 'Journal address', v: planInfo.journalEmailAddress || '', mono: true},
				{k: 'Mode', v: 'monitor & alert — delivery unchanged'}
			],
			note: 'Visibility-first mode: threats are detected and reported, but mail is not held before delivery.'
		})
	};

	switch (arch) {
		case dic.CONSTANTS.inboundSendingArchitecture.graph:
		case dic.CONSTANTS.inboundSendingArchitecture.mx:
		case dic.CONSTANTS.inboundSendingArchitecture.connector:
		case dic.CONSTANTS.inboundSendingArchitecture.journal:
			if (relay.is_connected) {
				return {state: 'connected', platform, platformLabel, spec: specs[arch]()};
			}
			break;
	}
	return {state: 'none', platform, platformLabel};
}

/* ============================ OUTBOUND ============================ */

export function buildOutboundFlow(planInfo: any, brand: string): EmailFlowResult {
	const platform = detectPlatform(planInfo);
	const platformLabel = PLAT_LABEL[platform];
	const relay = planInfo.outbound_relay || {};
	const arch = relay.sending_architecture || dic.CONSTANTS.sendingArchitecture.trustifi;
	const psBadge = planInfo.powershell?.enabled && platform === 'm365' ? 'PowerShell managed' : null;
	const shieldName = brand + ' Outbound Shield';

	const mtaRoutes = (relay.mtas || []).map(m => `${m.host}:${m.port || 25}`);
	const mtaFact = mtaRoutes.length ? mtaRoutes.slice(0, 2).join(', ') + (mtaRoutes.length > 2 ? ` +${mtaRoutes.length - 2}` : '') : null;
	const ipsFact = relay.static_ip_set_ips?.length ?
		relay.static_ip_set_ips.slice(0, 2).join(', ') + (relay.static_ip_set_ips.length > 2 ? ' …' : '') : null;
	const verifiedDomains = (planInfo.domains || []).filter(d => d.verified);
	const sendingDomainSub = verifiedDomains.length ?
		truncate(verifiedDomains[0].domain, 22) + (verifiedDomains.length > 1 ? ` +${verifiedDomains.length - 1}` : '') :
		'your verified domain';
	const onpremises = !!planInfo.exchange_server?.onpremises;
	const quarantineNode: FlowNode = {id: 'q', icon: 'quarantine', title: 'Outbound quarantine', tone: 'danger', w: 158, h: 78, x: 0, y: 0};

	const specs: {[arch: string]: () => FlowSpec} = {

		[dic.CONSTANTS.sendingArchitecture.trustifi]: () => ({
			aria: 'Outbound via ' + brand + ' flow', height: 330, archLabel: 'Relay via ' + brand,
			nodes: [
				platNode(platform, 20, 50, {sub: 'smart host / connector', badge: psBadge, w: 170}),
				{id: 'tru', x: 270, y: 50, icon: 'shield', title: shieldName, sub: 'DLP · rules · encryption', tone: 'trustifi', w: 190},
				{id: 'ses', x: 540, y: 50, icon: 'cloud', title: brand + ' delivery', sub: sendingDomainSub, sub2: 'DKIM · SPF aligned', tone: 'aws', w: 180},
				{id: 'rcp', x: 900, y: 54, icon: 'mailbox', title: 'Recipient', tone: 'success', w: 130, h: 84},
				{...quarantineNode, x: 286, y: 230}
			],
			edges: [
				{from: 'srv', to: 'tru', step: 1, label: 'via connector', dur: 2.8},
				{from: 'tru', to: 'ses', step: 2, dur: 2.8, delay: .7},
				{from: 'ses', to: 'rcp', step: 3, tone: 'success', label: 'sent from your domain', dur: 2.8, delay: 1.3},
				{from: 'tru', to: 'q', fromSide: 'bottom', toSide: 'top', tone: 'danger', dashed: true, step: '!', label: 'violations held', dots: 1, dur: 3.6}
			],
			steps: [
				{n: 1, t: `Your platform routes outgoing mail through a connector to ${brand}.`},
				{n: 2, t: `${brand} applies outbound rules — DLP scanning, encryption, tracking, signatures.`},
				{n: 3, t: 'Mail is sent from your verified domain with full DKIM and SPF alignment.'},
				{n: '!', t: 'Messages that violate policy are held in the outbound quarantine for review.', threat: true}
			],
			facts: [
				{k: 'Smart host', v: 'smtp.trustifi.com', mono: true},
				ipsFact ? {k: 'Sending IPs', v: ipsFact, mono: true} : null,
				{k: 'Verified domains', v: String(verifiedDomains.length)},
				{k: 'Applies', v: 'encryption · tracking · DLP rules'}
			].filter(Boolean),
			note: `${brand} delivers on your behalf from your own verified domain — recipients see your address, with authentication intact.`
		}),

		[dic.CONSTANTS.sendingArchitecture.client]: () => ({
			aria: 'Outbound via your MTA flow', height: 330, archLabel: 'Relay via your MTA',
			nodes: [
				platNode(platform, 20, 50, {sub: 'smart host / connector', badge: psBadge, w: 168}),
				{id: 'tru', x: 260, y: 50, icon: 'shield', title: shieldName, sub: 'DLP · rules · encryption', tone: 'trustifi', w: 188},
				{id: 'mta', x: 520, y: 50, icon: 'server', title: 'Your MTA', sub: truncate(mtaRoutes[0] || '', 26), tone: 'brand', w: 180},
				{id: 'rcp', x: 900, y: 54, icon: 'mailbox', title: 'Recipient', tone: 'success', w: 130, h: 84},
				{...quarantineNode, x: 275, y: 230}
			],
			edges: [
				{from: 'srv', to: 'tru', step: 1, dur: 2.8},
				{from: 'tru', to: 'mta', step: 2, label: 'to your MTA', dur: 2.8, delay: .7},
				{from: 'mta', to: 'rcp', step: 3, tone: 'success', label: 'your server delivers', dur: 2.8, delay: 1.3},
				{from: 'tru', to: 'q', fromSide: 'bottom', toSide: 'top', tone: 'danger', dashed: true, step: '!', dots: 1, dur: 3.6}
			],
			steps: [
				{n: 1, t: `Outgoing mail routes through a connector to ${brand} for processing.`},
				{n: 2, t: 'After DLP, rules and encryption, the message returns to your own MTA.'},
				{n: 3, t: 'Your server performs the final delivery — reputation and IP control stay with you.'},
				{n: '!', t: 'Policy violations are held in the outbound quarantine.', threat: true}
			],
			facts: [
				{k: 'Smart host', v: 'smtp.trustifi.com', mono: true},
				mtaFact ? {k: 'Return route', v: mtaFact, mono: true} : null,
				ipsFact ? {k: brand + ' IPs', v: ipsFact, mono: true} : null
			].filter(Boolean),
			note: `Best when you must keep delivery on your own infrastructure — ${brand} processes, your MTA sends.`
		}),

		[dic.CONSTANTS.sendingArchitecture.journal]: () => ({
			aria: 'Outbound journaling flow', height: 352, archLabel: 'Journaling (monitor)',
			nodes: [
				platNode(platform, 40, 42, {w: 180, onpremises}),
				{id: 'rcp', x: 830, y: 42, icon: 'mailbox', title: 'Recipient', tone: 'success', w: 150},
				{id: 'tru', x: 330, y: 222, icon: 'shield', title: shieldName, sub: truncate(planInfo.journalEmailAddress, 34), tone: 'trustifi', w: 216, h: 92},
				{id: 'act', x: 680, y: 230, icon: 'api', title: 'Insights & alerts', sub: 'sent-items visibility', tone: 'default', w: 160, h: 78}
			],
			edges: [
				{from: 'srv', to: 'rcp', step: 1, tone: 'success', label: 'delivery unchanged', dur: 2.8},
				{from: 'srv', to: 'tru', fromSide: 'bottom', toSide: 'top', step: 2, dashed: true, label: 'journal copy', dur: 3, delay: .6, labelAt: .45},
				{from: 'tru', to: 'act', step: 3, dots: 1, dur: 3.2, tone: 'muted'}
			],
			steps: [
				{n: 1, t: 'Your platform sends outbound mail directly — delivery is untouched.'},
				{n: 2, t: `A journaling rule sends a copy of every outgoing message to your ${brand} journal address.`},
				{n: 3, t: `${brand} scans the copies for data-loss and compliance insights and raises alerts.`}
			],
			facts: [
				{k: 'Journal address', v: planInfo.journalEmailAddress || '', mono: true},
				{k: 'Mode', v: 'monitor & alert — delivery unchanged'}
			],
			note: 'Monitoring mode — outbound mail is observed, not processed. Encryption and DLP enforcement need a relay architecture.'
		})
	};

	if (relay.enabled && specs[arch]) {
		return {state: 'connected', platform, platformLabel, spec: specs[arch]()};
	}
	if ((relay.key || relay.is_connected) && specs[arch]) {
		return {state: 'partial', platform, platformLabel, spec: specs[arch]()};
	}
	return {state: 'none', platform, platformLabel};
}

/* ====================== WIZARD PREVIEW SPECS ====================== */

// CC: maps wizard env key → platform token used by platNode
function envToPlat(envKey: string): 'm365' | 'google' | 'other' {
	if (envKey === 'exchange-cloud' || envKey === 'exchange-hybrid') {
		return 'm365';
	}
	if (envKey === 'g-suite') {
		return 'google';
	}
	return 'other';
}

const EMPTY_META = {archLabel: '', steps: [], facts: [], note: ''};

export function buildOutboundPreviewSpec(envKey: string, archKey: string): FlowSpec | null {
	const plat = envToPlat(envKey);
	const quarantineNode: FlowNode = {id: 'q', icon: 'quarantine', title: 'Outbound quarantine', tone: 'danger', w: 158, h: 78, x: 0, y: 0};

	const specs = {
		trustifi: (): FlowSpec => ({
			...EMPTY_META,
			aria: 'Outbound via Trustifi MTA preview', height: 330,
			nodes: [
				platNode(plat, 20, 50, {sub: 'smart host / connector', w: 170}),
				{id: 'tru', x: 270, y: 50, icon: 'shield', title: 'Outbound Shield', sub: 'DLP · rules · encryption', tone: 'trustifi', w: 190},
				{id: 'ses', x: 540, y: 50, icon: 'cloud', title: 'Trustifi delivery', sub: 'your-domain.com', sub2: 'DKIM · SPF aligned', tone: 'aws', w: 180},
				{id: 'rcp', x: 900, y: 54, icon: 'mailbox', title: 'Recipient', tone: 'success', w: 130, h: 84},
				{...quarantineNode, x: 286, y: 230},
			],
			edges: [
				{from: 'srv', to: 'tru', step: 1, label: 'via connector', dur: 2.8},
				{from: 'tru', to: 'ses', step: 2, dur: 2.8, delay: .7},
				{from: 'ses', to: 'rcp', step: 3, tone: 'success', label: 'sent from your domain', dur: 2.8, delay: 1.3},
				{from: 'tru', to: 'q', fromSide: 'bottom', toSide: 'top', tone: 'danger', dashed: true, step: '!', label: 'violations held', dots: 1, dur: 3.6},
			],
		}),
		client: (): FlowSpec => ({
			...EMPTY_META,
			aria: 'Outbound via your MTA preview', height: 330,
			nodes: [
				platNode(plat, 20, 50, {sub: 'smart host / connector', w: 168}),
				{id: 'tru', x: 260, y: 50, icon: 'shield', title: 'Outbound Shield', sub: 'DLP · rules · encryption', tone: 'trustifi', w: 188},
				{id: 'mta', x: 520, y: 50, icon: 'server', title: 'Your MTA', sub: 'your mail server', tone: 'brand', w: 180},
				{id: 'rcp', x: 900, y: 54, icon: 'mailbox', title: 'Recipient', tone: 'success', w: 130, h: 84},
				{...quarantineNode, x: 275, y: 230},
			],
			edges: [
				{from: 'srv', to: 'tru', step: 1, dur: 2.8},
				{from: 'tru', to: 'mta', step: 2, label: 'to your MTA', dur: 2.8, delay: .7},
				{from: 'mta', to: 'rcp', step: 3, tone: 'success', label: 'your server delivers', dur: 2.8, delay: 1.3},
				{from: 'tru', to: 'q', fromSide: 'bottom', toSide: 'top', tone: 'danger', dashed: true, step: '!', dots: 1, dur: 3.6},
			],
		}),
		journal: (): FlowSpec => ({
			...EMPTY_META,
			aria: 'Outbound journaling preview', height: 352,
			nodes: [
				platNode(plat, 40, 42, {w: 180}),
				{id: 'rcp', x: 830, y: 42, icon: 'mailbox', title: 'Recipient', tone: 'success', w: 150},
				{id: 'tru', x: 330, y: 222, icon: 'shield', title: 'Outbound Shield', sub: 'journal address', tone: 'trustifi', w: 216, h: 92},
				{id: 'act', x: 680, y: 230, icon: 'api', title: 'Insights & alerts', sub: 'sent-items visibility', tone: 'default', w: 160, h: 78},
			],
			edges: [
				{from: 'srv', to: 'rcp', step: 1, tone: 'success', label: 'delivery unchanged', dur: 2.8},
				{from: 'srv', to: 'tru', fromSide: 'bottom', toSide: 'top', step: 2, dashed: true, label: 'journal copy', dur: 3, delay: .6, labelAt: .45},
				{from: 'tru', to: 'act', step: 3, dots: 1, dur: 3.2, tone: 'muted'},
			],
		}),
	};

	return specs[archKey] ? specs[archKey]() : null;
}

export function buildInboundPreviewSpec(envKey: string, archKey: string): FlowSpec | null {
	const plat = envToPlat(envKey);
	const quarantineNode: FlowNode = {id: 'q', icon: 'quarantine', title: 'Quarantine', tone: 'danger', w: 132, h: 78, x: 0, y: 0};
	const platLabel = plat === 'm365' ? 'Microsoft 365' : plat === 'google' ? 'Google Workspace' : 'Your mail server';

	const specs = {
		mx: (): FlowSpec => ({
			...EMPTY_META,
			aria: 'Inbound MX preview', height: 330,
			nodes: [
				{id: 'snd', x: 20, y: 50, icon: 'sender', title: 'External sender', sub: 'any origin'},
				{id: 'tru', x: 250, y: 35, icon: 'shield', title: 'Inbound Shield', sub: 'inbound.your-domain.com', tone: 'trustifi', w: 190, step: 2, tags: ['Headers', 'Body', 'Links', 'Files', 'AI']},
				platNode(plat, 530, 50, {w: 170}),
				{id: 'box', x: 790, y: 50, icon: 'mailbox', title: 'Recipient mailbox', tone: 'success', w: 160},
				{...quarantineNode, x: 280, y: 240},
			],
			edges: [
				{from: 'snd', to: 'tru', step: 1, label: 'via MX', dur: 2.8},
				{from: 'tru', to: 'srv', step: 3, tone: 'success', label: 'clean mail', dur: 2.8, delay: .9},
				{from: 'srv', to: 'box', step: 4, tone: 'success', dur: 2.4, delay: 1.4},
				{from: 'tru', to: 'q', fromSide: 'bottom', toSide: 'top', tone: 'danger', dashed: true, step: '!', label: 'threats held', dots: 1, dur: 3.6},
				{from: 'q', to: 'srv', fromSide: 'right', toSide: 'bottom', tone: 'success', dashed: true, label: 'Release to MTA', elbow: true},
			],
		}),
		connector: (): FlowSpec => ({
			...EMPTY_META,
			aria: 'Inbound connector preview', height: 330,
			nodes: [
				{id: 'snd', x: 20, y: 50, icon: 'sender', title: 'External/Internal sender', sub: 'any origin', w: 140},
				platNode(plat, 220, 50, {w: 168}),
				{id: 'tru', x: 470, y: 35, icon: 'shield', title: 'Inbound Shield', sub: 'inbound-smtp.trustifi.com', tone: 'trustifi', w: 190, step: 3, tags: ['Headers', 'Body', 'Links', 'Files', 'AI']},
				platNode(plat, 740, 50, {id: 'srv2', title: platLabel, sub: 'inbound connector', w: 168}),
				{id: 'box', x: 960, y: 54, icon: 'mailbox', title: 'Mailbox', tone: 'success', w: 90, h: 84},
				{...quarantineNode, x: 500, y: 240},
			],
			edges: [
				{from: 'snd', to: 'srv', step: 1, label: 'MX unchanged', dur: 2.6},
				{from: 'srv', to: 'tru', step: 2, label: 'via connector', dur: 2.6, delay: .6},
				{from: 'tru', to: 'srv2', step: 4, tone: 'success', label: 'returned clean', dur: 2.6, delay: 1.2},
				{from: 'srv2', to: 'box', tone: 'success', dots: 1, dur: 2.2, delay: 1.8},
				{from: 'tru', to: 'q', fromSide: 'bottom', toSide: 'top', tone: 'danger', dashed: true, step: '!', label: 'threats held', dots: 1, dur: 3.6},
				{from: 'q', to: 'srv2', fromSide: 'right', toSide: 'bottom', tone: 'success', dashed: true, label: 'Release to MTA', elbow: true},
			],
		}),
		graph: (): FlowSpec => ({
			...EMPTY_META,
			aria: 'Inbound API preview', height: 352,
			nodes: [
				{id: 'snd', x: 20, y: 42, icon: 'sender', title: 'External/Internal sender', sub: 'any origin', w: 150},
				platNode(plat, 300, 42, {w: 180}),
				{id: 'box', x: 830, y: 42, icon: 'mailbox', title: 'Recipient mailbox', tone: 'success', w: 160},
				{id: 'tru', x: 310, y: 222, icon: 'shield', title: 'Inbound Shield', sub: 'Graph subscription (webhook)', tone: 'trustifi', w: 200, h: 92},
				{...quarantineNode, x: 640, y: 230},
			],
			edges: [
				{from: 'snd', to: 'srv', step: 1, dur: 2.6},
				{from: 'srv', to: 'box', step: 2, tone: 'success', label: 'delivered immediately', dur: 2.6, delay: .5},
				{from: 'srv', to: 'tru', fromSide: 'bottom', toSide: 'top', step: 3, label: 'new-mail notification', dur: 2.8, delay: .9, labelAt: .45},
				{from: 'tru', to: 'q', tone: 'danger', dashed: true, step: '!', label: 'moved to quarantine', dots: 1, dur: 3.4},
			],
		}),
		journal: (): FlowSpec => ({
			...EMPTY_META,
			aria: 'Inbound journaling preview', height: 352,
			nodes: [
				{id: 'snd', x: 20, y: 42, icon: 'sender', title: 'External/Internal sender', sub: 'any origin', w: 150},
				platNode(plat, 300, 42, {w: 180}),
				{id: 'box', x: 830, y: 42, icon: 'mailbox', title: 'Recipient mailbox', tone: 'success', w: 160},
				{id: 'tru', x: 310, y: 222, icon: 'shield', title: 'Inbound Shield', sub: 'journal address', tone: 'trustifi', w: 216, h: 92},
				{id: 'act', x: 680, y: 230, icon: 'api', title: 'Threat response', sub: 'alerts · remediation', tone: 'danger', w: 150, h: 78},
			],
			edges: [
				{from: 'snd', to: 'srv', step: 1, dur: 2.6},
				{from: 'srv', to: 'box', step: 2, tone: 'success', label: 'delivered normally', dur: 2.6, delay: .5},
				{from: 'srv', to: 'tru', fromSide: 'bottom', toSide: 'top', step: 3, dashed: true, label: 'journal copy', dur: 2.8, delay: .9, labelAt: .45},
				{from: 'tru', to: 'act', tone: 'danger', dashed: true, step: 4, dots: 1, dur: 3.4},
			],
		}),
	};

	return specs[archKey] ? specs[archKey]() : null;
}
